import {useEffect, useMemo, useRef, useState} from 'react';
import store from "../../cpac-store.ts";
import {Language} from "../../language/Language.ts";
import {getLabelForLang} from "../../language/lang-utils.ts";
import {config} from "../../config.ts";
import {JwPlaylistItem} from "../../types/JwPlaylistItem.ts";
import {setElementAttributeByClassname} from "../../utils/common-utils.ts";
import {
    AudioLang,
    audioAvailabilityMessageKey,
    audioDebug,
    audioLangsOf,
    buildStreamUrl,
    ManifestAudioTrack,
    normalizeAudioLang,
    PlayerAudioTrack,
    playerTrackLang,
    probeMasterAudioTracks,
    sameAudioLangs,
} from "./audio-tracks.ts";
import {AudioTrackReconciler} from "./audio-track-reconciler.ts";
import {AudioLanguageSwitcher} from "./audio-language-switcher.ts";

export type CpacJwPlayerContainerProps = {
    vType: string,
    // The language the audio should be on. Initially the page language; changed by the buttons.
    // The reconciler keeps re-asserting this against the player until the player agrees.
    desiredLang: AudioLang,
    // Incremented by the parent on every language-button click — playback must (re)start on click.
    playSignal: number,
    playlist: JwPlaylistItem,
    setShowCPACTV: ((show: boolean) => void) | null,
    autoplay: boolean,
    // The audio renditions declared in the master manifest — drives the language buttons.
    onTracks: (tracks: ManifestAudioTrack[]) => void,
    // The confirmed active track's language — drives the buttons' selected state.
    onActiveLang: (lang: AudioLang | undefined) => void,
    // The user picked a track in JW's own gear menu — the parent should make it the new
    // desiredLang (Video.tsx owns the desired language; the player side never overrides it).
    onAdoptLang: (lang: AudioLang) => void,
    onMessage: (message: string | undefined) => void,
    // Reports whether the JW player is currently playing. The featured view uses this to
    // defer replacing the active source until playback is paused or complete.
    onPlaybackState?: (isPlaying: boolean) => void,
    // Fired once the JW player has set up (onReady) — lets the page fade out its loading shimmer.
    onPlayerReady?: () => void,
}

// The slice of the JW player API this container uses (the library ships no TS types).
type JwPlayerEvent = { oldstate?: string, currentTrack?: number };
type JwPlayer = {
    setup: (options: Record<string, unknown>) => void;
    remove: () => void;
    getAudioTracks: () => PlayerAudioTrack[] | undefined;
    getCurrentAudioTrack: () => number;
    setCurrentAudioTrack: (index: number) => void;
    on: (event: string, handler: (event?: JwPlayerEvent) => void) => void;
    once: (event: string, handler: (event?: JwPlayerEvent) => void) => void;
    play: () => void;
    load: (playlist: JwPlaylistItem[]) => void;
    getPosition: () => number;
    seek: (seconds: number) => void;
    getProvider?: () => { name?: string } | undefined;
};
type JwGlobal = (id: string) => JwPlayer;

// The JW library itself (version managed in the JW dashboard) is loaded straight from
// config.jwPlayerScript. The script tag is shared by id, so the library loads exactly once
// no matter how many players mount on the page.
const JW_SCRIPT_ID = "jw-player-script";

const loadJwPlayerScript = (src: string): Promise<void> =>
    new Promise((resolve, reject) => {
        if ((window as unknown as { jwplayer?: JwGlobal }).jwplayer) {
            resolve();
            return;
        }
        const existing = document.getElementById(JW_SCRIPT_ID) as HTMLScriptElement | null;
        const script = existing ?? document.createElement("script");
        script.addEventListener("load", () => resolve(), { once: true });
        script.addEventListener("error", () => reject(new Error(`JW player script failed: ${src}`)), { once: true });
        if (!existing) {
            script.id = JW_SCRIPT_ID;
            script.async = true;
            script.src = src;
            document.head.appendChild(script);
        }
    });

// Live streams can grow/lose audio renditions mid-event; how often to re-read the master
// manifest while actually playing (never for VOD, never while paused).
const LIVE_TRACK_RECHECK_MS = 10000;

// Unique DOM id per player instance (several can mount on one page).
let playerInstanceCounter = 0;
const nextPlayerId = (): string => `pId-${++playerInstanceCounter}`;

/**
 * The JW player adapter (CPACWEB-420): owns the player lifecycle (script → setup → events →
 * remove) and translates JW events into the framework-free AudioTrackReconciler, which owns
 * all audio-track correctness. The desired language itself lives in Video.tsx.
 *
 * JW listeners are registered once per setup() (JW drops them on re-setup), NOT per render,
 * so handlers never read render closures: current props/playlist come through `latest`
 * (the latest-props ref pattern).
 *
 * Architecture doc: doc/video-player-audio-architecture.md
 */
export const CpacJwPlayerContainer = (props: CpacJwPlayerContainerProps) => {
    const [playerID] = useState<string>(nextPlayerId);

    // The page's language, fixed per page load — the stream-URL seam keys off it.
    const pageLang: AudioLang = normalizeAudioLang(store.getState().lang.toString());
    const streamUrl = props.playlist.file ? buildStreamUrl(props.playlist.file, pageLang) : undefined;
    const jwPlaylist = useMemo(
        () => ({...props.playlist, file: streamUrl ?? props.playlist.file}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [streamUrl, props.playlist.title, props.playlist.image],
    );

    // Latest props + derived values, for handlers that outlive the render they were created
    // in (JW listeners, timers, the live interval). One ref, one sync effect.
    const latest = useRef({ props, streamUrl, jwPlaylist });
    useEffect(() => {
        latest.current = { props, streamUrl, jwPlaylist };
    });

    const isPlayingRef = useRef(false);
    const initializedRef = useRef(false);
    const manifestTracksRef = useRef<ManifestAudioTrack[] | null>(null);
    // Membership snapshot of the PLAYER's track list — JW re-fires 'audioTracks' during any
    // switch; only a real membership change is turbulence (else gear picks classify wrong).
    const playerTrackLangsRef = useRef<string>("");

    const getPlayer = (): JwPlayer | null => {
        // jwplayer is provided globally by the JW library script at runtime
        const jw = (window as unknown as { jwplayer?: JwGlobal }).jwplayer;
        if (!jw) return null;
        const player = jw(playerID);
        return player && typeof player.getAudioTracks === "function" ? player : null;
    };

    // The reconciliation state machine — created once per mount; survives source re-setups
    // (sourceChanged() resets its selection state). Its dependencies read `latest`, so the
    // machine always sees current props without re-wiring.
    const reconcilerRef = useRef<AudioTrackReconciler | null>(null);
    if (reconcilerRef.current === null) {
        reconcilerRef.current = new AudioTrackReconciler({
            getPlayer: () => getPlayer(),
            getDesiredLang: () => latest.current.props.desiredLang,
            onActiveLang: (lang) => latest.current.props.onActiveLang(lang),
            onAdoptLang: (lang) => latest.current.props.onAdoptLang(lang),
        });
    }
    const reconciler = reconcilerRef.current;

    // ---- manifest probe (buttons + audio-availability message) ----

    // The single sink for a fresh read of the master manifest's audio renditions (the
    // initial probe and the live 10s re-probe both land here). The MANIFEST — not the
    // player — drives the language buttons, because with preload "none" the player doesn't
    // know its tracks until playback starts and the buttons must exist before that.
    // (Switching, by contrast, always consults the player's own live track list at command
    // time.) Three effects, in order: remember the snapshot (the live re-probe diffs
    // against it to detect renditions appearing/disappearing mid-event), hand the parent
    // the button list, and derive the "only the other language is available" notice for
    // the current page.
    const publishManifestTracks = (tracks: ManifestAudioTrack[]) => {
        manifestTracksRef.current = tracks;
        latest.current.props.onTracks(tracks);
        const messageKey = audioAvailabilityMessageKey(tracks, pageLang);
        latest.current.props.onMessage(
            messageKey === undefined
                ? undefined
                : getLabelForLang(messageKey, messageKey === "FRE_AUDIO_ONLY" ? Language.en : Language.fr),
        );
    };

    useEffect(() => {
        if (!streamUrl) return;
        let cancelled = false;
        probeMasterAudioTracks(streamUrl).then((tracks) => {
            if (cancelled || tracks.length === 0) return;
            audioDebug("manifest probe", tracks);
            publishManifestTracks(tracks);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [streamUrl]);

    // Live streams can grow/lose audio renditions mid-event (interpretation channels coming
    // online). No player re-fetches the MASTER manifest mid-session, so a source reload is
    // the only way to surface them — a PLAYER-level reload, never a page reload. Only while
    // actually playing, and never for VOD (whose manifests don't change).
    useEffect(() => {
        if (props.vType === "video") return;
        const interval = setInterval(async () => {
            const url = latest.current.streamUrl;
            if (!isPlayingRef.current || !url) return;
            const fresh = await probeMasterAudioTracks(url);
            const prev = manifestTracksRef.current;
            if (fresh.length === 0 || prev === null) return; // probe failure / not yet primed
            if (sameAudioLangs(fresh, prev)) return;
            console.info(
                `[CPACWEB-420] live audio tracks changed (${audioLangsOf(prev).join(",")} → ${audioLangsOf(fresh).join(",")}) — reloading player`,
            );
            publishManifestTracks(fresh);
            const player = getPlayer();
            if (player) {
                reconciler.sourceChanged();
                playerTrackLangsRef.current = "";
                player.load([latest.current.jwPlaylist]);
                player.play();
            }
        }, LIVE_TRACK_RECHECK_MS);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.vType]);

    // ---- prop-driven triggers ----

    // HOW a language change executes is platform-dependent (in-place on MSE, source reload
    // on the native HLS provider) — that policy lives in AudioLanguageSwitcher; see its
    // header for the on-device findings that force the reload strategy.
    const switcherRef = useRef<AudioLanguageSwitcher | null>(null);
    if (switcherRef.current === null) {
        switcherRef.current = new AudioLanguageSwitcher({
            getPlayer: () => getPlayer(),
            getVideoElement: () =>
                document.querySelector(`#${playerID} video`) as
                    (HTMLVideoElement & { audioTracks?: { length: number } }) | null,
            getPlaylistItem: () => latest.current.jwPlaylist,
            getBaseFile: () => latest.current.props.playlist.file,
            isVod: () => latest.current.props.vType === "video",
            reconciler,
            onSourceReloaded: () => {
                playerTrackLangsRef.current = "";
            },
        });
    }
    const switcher = switcherRef.current;

    useEffect(() => {
        switcher.apply(props.desiredLang, initializedRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.desiredLang]);

    useEffect(() => {
        if (props.playSignal > 0) {
            const player = getPlayer();
            if (player && typeof player.play === "function") player.play();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.playSignal]);

    // iOS re-runs its audio auto-selection when the page returns to the foreground.
    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") reconciler.handleTrigger("foreground");
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- player lifecycle ----

    // Set up (or re-set up) the JW instance and register EVERY listener. JW drops listeners
    // on re-setup, so registration lives here, not in onReady. A re-setup happens when the
    // source actually changes (prelive → live URL swap), keyed by the jwPlaylist memo.
    const initPlayer = () => {
        const jw = (window as unknown as { jwplayer?: JwGlobal }).jwplayer;
        if (!jw) return;
        const player = jw(playerID);
        if (!player || typeof player.setup !== "function") return;
        if (initializedRef.current) {
            // New source: the old selection state means nothing now.
            reconciler.sourceChanged();
            playerTrackLangsRef.current = "";
        }
        initializedRef.current = true;
        player.setup({
            key: config.jwPlayerKey,
            playlist: [latest.current.jwPlaylist],
            autostart: props.autoplay,
            mute: false,
            // Don't fetch the manifest or media (audio + video segments) while the
            // player sits paused in a featured tile — load only on user play. Measured:
            // JW's default preload "metadata" pulled an audio .aac + a video .ts segment
            // while paused; "none" pulls nothing until play, and the poster still shows.
            // Safe with autostart — when autoplay is true, JW loads on autostart anyway.
            preload: "none",
            intl: {
                en: {
                    errors: {
                        badConnection: getLabelForLang("VIDEO_ERROR_BAD_CONNECTION", Language.en),
                        cantLoadPlayer: getLabelForLang("VIDEO_ERROR_LOAD_FAILURE", Language.en),
                        cantPlayInBrowser: getLabelForLang("VIDEO_ERROR_BROWSER", Language.en),
                        cantPlayVideo: getLabelForLang("GENERIC_VIDEO_PLAYER_ERROR", Language.en),
                        liveStreamDown: getLabelForLang("VIDEO_ERROR_STREAM_ENDED", Language.en),
                        protectedContent: getLabelForLang("VIDEO_ERROR_PROTECTED", Language.en),
                        technicalError: getLabelForLang("VIDEO_ERROR_TECHNICAL", Language.en),
                    },
                },
                fr: {
                    errors: {
                        badConnection: getLabelForLang("VIDEO_ERROR_BAD_CONNECTION", Language.fr),
                        cantLoadPlayer: getLabelForLang("VIDEO_ERROR_LOAD_FAILURE", Language.fr),
                        cantPlayInBrowser: getLabelForLang("VIDEO_ERROR_BROWSER", Language.fr),
                        cantPlayVideo: getLabelForLang("GENERIC_VIDEO_PLAYER_ERROR", Language.fr),
                        liveStreamDown: getLabelForLang("VIDEO_ERROR_STREAM_ENDED", Language.fr),
                        protectedContent: getLabelForLang("VIDEO_ERROR_PROTECTED", Language.fr),
                        technicalError: getLabelForLang("VIDEO_ERROR_TECHNICAL", Language.fr),
                    },
                },
            },
        });
        player.on("ready", () => {
            setElementAttributeByClassname("jw-logo", "role", "img");
            setElementAttributeByClassname("jw-logo", "tabindex", "-1");
            reconciler.handleTrigger("ready");
            if (latest.current.props.onPlayerReady) latest.current.props.onPlayerReady();
        });
        // JW fires a single 'play' event for both first play and resume; event.oldstate
        // distinguishes them.
        player.on("play", (event?: JwPlayerEvent) => {
            isPlayingRef.current = true;
            if (latest.current.props.onPlaybackState) latest.current.props.onPlaybackState(true);
            if (latest.current.props.setShowCPACTV) latest.current.props.setShowCPACTV(false);
            reconciler.handleTrigger(event?.oldstate === "paused" ? "resume" : "play");
            fixAudioTracksButtonLabels();
        });
        player.on("pause", () => {
            isPlayingRef.current = false;
            if (latest.current.props.onPlaybackState) latest.current.props.onPlaybackState(false);
            if (latest.current.props.setShowCPACTV) latest.current.props.setShowCPACTV(true);
        });
        player.on("complete", () => {
            isPlayingRef.current = false;
            if (latest.current.props.onPlaybackState) latest.current.props.onPlaybackState(false);
        });
        player.on("audioTracks", () => {
            // JW re-fires this during ANY track switch; only a genuine membership change
            // (first population, live rendition add/remove) counts as turbulence — otherwise
            // a user's gear-menu pick would always classify as a platform re-assertion.
            const langs = (getPlayer()?.getAudioTracks() ?? []).map(playerTrackLang).sort().join(",");
            if (langs !== playerTrackLangsRef.current) {
                playerTrackLangsRef.current = langs;
                reconciler.handleTrigger("tracks-populated");
            } else {
                audioDebug("audioTracks re-fired, membership unchanged — not turbulence");
            }
            fixAudioTracksButtonLabels();
        });
        // The event payload index is authoritative — the read-back can still report the OLD
        // track while a switch is in flight.
        player.on("audioTrackChanged", (event?: JwPlayerEvent) => reconciler.handleTrackChanged(event?.currentTrack));
        // The moment initial playback truly starts — JW settles its own initial track
        // selection around here and emits no audioTrackChanged for it.
        player.on("firstFrame", () => reconciler.handleTrigger("first-frame"));
        player.on("seeked", () => reconciler.handleTrigger("seeked"));
    };

    // Load the JW library once and (re)initialize whenever the source actually changes.
    useEffect(() => {
        let cancelled = false;
        loadJwPlayerScript(config.jwPlayerScript)
            .then(() => {
                if (!cancelled) initPlayer();
            })
            .catch((e) => console.warn("[CPACWEB-420] JW player script failed to load", e));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jwPlaylist]);

    // Tear down on unmount: silence the machine, then remove the JW instance.
    useEffect(() => {
        return () => {
            reconciler.dispose();
            try {
                const jw = (window as unknown as { jwplayer?: JwGlobal }).jwplayer;
                if (jw && initializedRef.current) jw(playerID).remove();
            } catch {
                // instance already gone — nothing to clean up
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // per Vualto: "It's a bit of a hack but, it's a way to ensure that the tracks are always
    // labelled correctly." Relabels JW's own gear-menu entries with our localized labels.
    const fixAudioTracksButtonLabels = () => {
        const labelFor = (key: string, fallback: string): string => {
            const lang = store.getState().lang;
            const label = lang === Language.en || lang === Language.fr
                ? getLabelForLang(key, lang)
                : undefined;
            return label === undefined ? fallback : label;
        };

        const langBtns = document.querySelectorAll('.jw-settings-submenu-audioTracks .jw-settings-content-item');
        langBtns.forEach(btn => {
            if (btn instanceof HTMLElement) {
                // save this once - we will need it when we re-render
                if (!btn.dataset.lang) {
                    switch (btn.innerHTML.toLowerCase()) {
                        case "english":
                            btn.dataset.lang = "eng";
                            break;
                        case "french":
                            btn.dataset.lang = "fre";
                            break;
                        case "floor":
                            btn.dataset.lang = "mul";
                            break;
                        default:
                            btn.dataset.lang = "mul"; // usually the file name
                    }
                }
                let newLabel;
                switch (btn.dataset.lang) {
                    case "eng":
                        newLabel = labelFor("ENGLISH", "eng");
                        break;
                    case "fre":
                        newLabel = labelFor("FRENCH", "fre");
                        break;
                    case "mul":
                        newLabel = labelFor("FLOOR", "mul");
                        break;
                    default:
                        newLabel = btn.dataset.lang;
                }

                btn.ariaLabel = newLabel;
                btn.innerHTML = newLabel;
            }
        });
    };

    if (props.playlist.file == null || !config.jwPlayerKey || !config.jwPlayerScript) {
        return null;
    }

    // JW replaces this div with the player UI on setup().
    return <div id={playerID} />;
};
